import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faListCheck } from '@fortawesome/pro-regular-svg-icons';
import {
  faCalendarDay,
  faCheckCircle,
  faLocationDot,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CheckIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from 'urql';
import { Files } from '~/components/FileLink';
import { ReadOnlyEditor } from '~/components/form/editor/ReadOnlyEditor';
import { deserializeNotes } from '~/components/job-template/JobTemplateForm';
import { MemoGallery } from '~/components/job/JobView/Feed/Gallery';
import { TaskIcon } from '~/components/job/JobView/TaskList/TaskListItem';
import { TagsBuilder } from '~/components/TagCombobox/TagsBuilder';
import { Avatar } from '~/components/ui/Avatar';
import { Bubble, Section } from '~/components/ui/nucleus';
import { ReadValue } from '~/components/ui/ReadValue';
import { StatusBadge } from '~/components/ui/StatusBadge';
import { ViewCard } from '~/components/ui/ViewCard';
import {
  formatMonthlyRepeat,
  normalizeByDay,
  normalizeFreqForDisplay,
} from '~/components/automation/automationRepeat';
import {
  AttributeSelectDocument,
  ListAutomationsQuery,
  Status,
} from '~/gql/graphql';
import { formatDate, formatTime } from '~/helpers/formatDate';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { AutomationMenu } from './AutomationMenu';

type Props = {
  automation: ListAutomationsQuery['automations'][number];
};

// Type extension for Automation with repeat fields
type AutomationWithRepeat = ListAutomationsQuery['automations'][number] & {
  repeatStartDate?: string | null;
  repeat?: {
    freq: string;
    interval: string;
    byday?: string[] | null;
    bymonthday?: string[] | null;
  } | null;
  maintainRepeats?: number | null;
};

type WhenBubbleProps = {
  automation: AutomationWithRepeat;
};

function WhenBubble({ automation }: WhenBubbleProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const trigger = automation.on === 'repeat' ? 'date' : 'booking';

  // Compute formatted repeat patterns once
  const freq = normalizeFreqForDisplay(automation.repeat?.freq);
  const formattedMonthlyRepeat =
    freq === 'monthly'
      ? formatMonthlyRepeat(
          automation.repeat?.bymonthday,
          automation.repeat?.byday
        )
      : null;
  const normalizedWeeklyDays =
    freq === 'weekly' ? normalizeByDay(automation.repeat?.byday) : null;

  return (
    <Bubble>
      <Section className={classNames(isMobile && 'px-3')}>
        <div className='flex flex-col !items-start gap-1.5'>
          {trigger === 'booking' ? (
            <>
              <div className='flex items-center gap-1.5 font-semibold leading-[17.5px]'>
                <FontAwesomeIcon
                  color='#005959'
                  size='sm'
                  icon={faCheckCircle}
                />
                {t(`automations.events.${automation.on}`)}
              </div>
              <p className='font-normal text-secondary'>
                {automation.day === 0
                  ? 'Same day'
                  : automation.day < 0
                  ? `${Math.abs(automation.day)} day${
                      Math.abs(automation.day) > 1 ? 's' : ''
                    } before`
                  : `${automation.day} day${
                      automation.day > 1 ? 's' : ''
                    } after`}
                {automation.time &&
                  (() => {
                    const formattedTime = formatTime(automation.time);
                    return formattedTime ? ` at ${formattedTime}` : '';
                  })()}
              </p>
            </>
          ) : (
            <>
              <div className='flex items-center gap-1 font-semibold leading-[17.5px]'>
                <FontAwesomeIcon
                  color='#005959'
                  size='sm'
                  icon={faCalendarDay}
                />
                Repeats
              </div>
              <p className='font-normal text-secondary'>
                Every {automation.repeat?.interval}{' '}
                {t(`repeatFrequencies.${freq}`)}
              </p>
              {formattedMonthlyRepeat && (
                <p className='font-normal text-secondary'>
                  On {formattedMonthlyRepeat}
                </p>
              )}
              {normalizedWeeklyDays && (
                <p className='font-normal text-secondary'>
                  On {normalizedWeeklyDays.join(', ')}
                </p>
              )}
              {automation.repeatStartDate &&
                (() => {
                  // Use validated formatDate helper instead of toLocaleDateString
                  const formattedDate = formatDate(
                    automation.repeatStartDate,
                    'PP'
                  );
                  if (!formattedDate) {
                    return null;
                  }
                  return (
                    <p className='font-normal text-secondary'>
                      Starts {formattedDate}
                      {automation.time &&
                        (() => {
                          const formattedTime = formatTime(automation.time);
                          return formattedTime ? ` at ${formattedTime}` : '';
                        })()}
                    </p>
                  );
                })()}
              {automation.maintainRepeats && (
                <p className='font-normal text-secondary'>
                  Maintains {automation.maintainRepeats} repeat(s)
                </p>
              )}
            </>
          )}
        </div>
      </Section>
    </Bubble>
  );
}

type WhereBubbleProps = {
  automation: AutomationWithRepeat;
};

function WhereBubble({ automation }: WhereBubbleProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  return (
    <Bubble>
      <Section className={classNames(isMobile && 'px-3')}>
        <div className='flex flex-col !items-start gap-1.5'>
          <div className='flex items-center gap-1.5 font-semibold leading-[17.5px]'>
            <FontAwesomeIcon color='#005959' size='sm' icon={faLocationDot} />
            {t(`automations.whereTypes.${automation.whereType}`)}
          </div>
          {automation.sites.length > 0 && (
            <div className='flex flex-col gap-1'>
              {automation.sites.map(({ id, name }) => (
                <p key={id} className='font-normal text-secondary'>
                  {name}
                </p>
              ))}
            </div>
          )}
        </div>
      </Section>
    </Bubble>
  );
}

type WhoBubbleProps = {
  automation: AutomationWithRepeat;
};

function WhoBubble({ automation }: WhoBubbleProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  return (
    <Bubble>
      {automation.owners && automation.owners.length > 0 && (
        <div>
          <p className='mb-[3px] text-sm'>
            {t('jobOwner', { count: automation.owners.length })}:
          </p>
          <div className='flex flex-col gap-2'>
            {automation.owners.map((owner) => (
              <div
                key={owner.id}
                className={classNames(
                  'flex items-center rounded-md border border-outline-variant bg-white',
                  isMobile ? 'gap-1.5 p-2' : 'gap-4 px-3 py-2'
                )}
              >
                <Avatar
                  className='h-11 w-11'
                  name={owner.name}
                  image={owner.image}
                  size='full'
                />
                <p className='font-medium'>{owner.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {automation.assignees && automation.assignees.length > 0 && (
        <div>
          <p className='mb-[3px] text-sm'>Assigned to:</p>
          <div className='flex flex-col gap-2'>
            {automation.assignees.map((assignee) => (
              <div
                key={assignee.id}
                className={classNames(
                  'flex items-center rounded-md border border-outline-variant bg-white',
                  isMobile ? 'gap-1.5 p-2' : 'gap-4 px-3 py-2'
                )}
              >
                <Avatar
                  className='h-11 w-11'
                  name={assignee.name}
                  image={assignee.image}
                  size='full'
                />
                <p className='font-medium'>{assignee.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Bubble>
  );
}

type TemplateData = NonNullable<
  ListAutomationsQuery['automations'][number]['template']
> & {
  id: string;
  status: Status;
  name: string;
  tags?: Array<{
    id: string;
    entityType: string;
    category?: string | null;
    name: string;
    description?: string | null;
    colour: string;
  }> | null;
  notes?: string | null;
  includeAttributes?: Array<string> | null;
  jobAttributeIds?: Array<string> | null;
  attachments?: Array<{
    id: string;
    kind: string;
    ext: string;
    thumb: string;
    url: string;
    originalFilename: string;
  }> | null;
  guestyCleaningStatus?: string | null;
  tasks: Array<{
    id: string;
    type: string;
    name: string;
    description: string;
  }>;
};

type WhatBubbleProps = {
  automation: AutomationWithRepeat;
  templateData: TemplateData;
  siteAttributes?: Array<{
    id: string;
    name: string;
    category?: string | null;
  }>;
  jobAttributes?: Array<{ id: string; name: string; category?: string | null }>;
};

function WhatBubble({
  automation,
  templateData: templateDataProp,
  siteAttributes,
  jobAttributes,
}: WhatBubbleProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  // Type assertion to work around GraphQL fragment type inference
  const templateData = templateDataProp as TemplateData;

  const notesContent = useMemo(() => {
    if (!templateData.notes) return null;
    try {
      return deserializeNotes(templateData.notes);
    } catch {
      return null;
    }
  }, [templateData.notes]);

  return (
    <Bubble>
      <Section className='px-3'>
        <div className='w-full overflow-x-hidden'>
          <div className='flex flex-col gap-4'>
            <div>
              <p className='mb-1 text-sm text-secondary'>Job Name</p>
              <p className='text-primary'>{automation.jobName}</p>
            </div>
          </div>
        </div>
      </Section>

      <Section className='px-3'>
        <p className='mb-1 w-full text-sm text-primary'>Template</p>
        <div className='mb-5 flex w-full items-center justify-between rounded-md border border-outline-variant py-3 pl-2 pr-1'>
          <div className='flex flex-1 items-center gap-2.5'>
            <div className='flex items-start gap-2.5 rounded-sm bg-[rgba(0,106,106,0.14)] p-1'>
              <FontAwesomeIcon
                icon={faListCheck as IconProp}
                className='flex h-5 w-5 items-center justify-center text-brand'
              />
            </div>
            {templateData.name}
          </div>
        </div>

        <div className='-mb-2 flex flex-col !items-start gap-3'>
          {templateData.tags && templateData.tags.length > 0 && (
            <div>
              <ReadValue label='tag_plural'>
                <div className='flex flex-[1_0_0] flex-wrap items-center justify-start gap-1.5'>
                  <TagsBuilder tags={templateData.tags} />
                </div>
              </ReadValue>
            </div>
          )}

          {notesContent && (
            <div>
              <ReadValue label='notes'>
                <ReadOnlyEditor content={notesContent} />
              </ReadValue>
            </div>
          )}

          {templateData.attachments && templateData.attachments.length > 0 && (
            <div>
              <div className='label mb-1.5'>
                {t('attachment', {
                  count: templateData.attachments.length,
                })}
              </div>
              <Files attachments={templateData.attachments} />
              <MemoGallery attachments={templateData.attachments} />
            </div>
          )}

          {templateData.guestyCleaningStatus && (
            <div>
              <ReadValue label='Guesty cleaning status'>
                <div className='flex items-center gap-2'>
                  <CheckIcon className='h-5 w-5 text-brand' />
                  Update Guesty cleaning status
                </div>
              </ReadValue>
            </div>
          )}
        </div>
      </Section>

      {(templateData.includeAttributes?.length ?? 0) > 0 && (
        <Section className={classNames(isMobile && 'px-3')}>
          <div className='flex flex-col !items-start gap-1'>
            <p className='mb-1 text-sm font-semibold'>Place Attributes</p>
            {templateData.includeAttributes?.map((attribute) => {
              const found = siteAttributes?.find(({ id }) => id === attribute);
              if (!found) {
                return null;
              }
              return (
                <p className='text-sm text-secondary' key={found.id}>
                  {(found.category ? `${found.category} \\ ` : '') + found.name}
                </p>
              );
            })}
          </div>
        </Section>
      )}

      {templateData.jobAttributeIds &&
        templateData.jobAttributeIds.length > 0 && (
          <Section className={classNames(isMobile && 'px-3')}>
            <div className='flex flex-col !items-start gap-1'>
              <p className='mb-1 text-sm font-semibold'>Custom Fields</p>
              {templateData.jobAttributeIds.map((attribute) => {
                const found = jobAttributes?.find(({ id }) => id === attribute);
                if (!found) {
                  return null;
                }
                return (
                  <p className='text-sm text-secondary' key={found.id}>
                    {(found.category ? `${found.category} \\ ` : '') +
                      found.name}
                  </p>
                );
              })}
            </div>
          </Section>
        )}

      {templateData.tasks.length > 0 && (
        <Section className='px-3'>
          <div className='w-full'>
            <p className='mb-2 text-sm leading-5 text-secondary'>
              Tasks preview
            </p>
            <div>
              <ul className='mb-1'>
                {templateData.tasks.map((task) => (
                  <li
                    key={task.id}
                    className='mb-3 flex w-full items-center gap-3'
                  >
                    <Link
                      to={`/automations/${automation.id}/tasks/${task.id}`}
                      className='relative w-full'
                    >
                      <div
                        className={classNames(
                          'flex flex-1 items-center rounded border bg-white text-grey-20 shadow transition-all hover:text-grey-40 hover:shadow-md',
                          'border-grey-10'
                        )}
                      >
                        <div className='flex flex-1 cursor-pointer p-2.5'>
                          <p className='pr-[35px] text-grey-50'>{task.name}</p>
                        </div>
                        <div className='absolute right-3 top-0 flex h-full items-center justify-center text-grey-40'>
                          <TaskIcon
                            taskType={task.type}
                            taskDescription={task.description}
                          />
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      )}
    </Bubble>
  );
}

export function AutomationView({ automation: automationProp }: Props) {
  const { isMobile } = useBreakpoint();

  // Cast to include repeat fields
  const automation = automationProp as AutomationWithRepeat;

  // Template data is already in the correct format from the query
  // Use type assertion since we know template exists and has the fields we queried
  const templateData = automation.template as TemplateData;

  // Fetch attributes for display
  const [siteAttributesResult] = useQuery({
    query: AttributeSelectDocument,
    variables: { entityType: 'Site' },
    requestPolicy: 'cache-first',
  });
  const siteAttributes = siteAttributesResult.data?.attributes;

  const [jobAttributesResult] = useQuery({
    query: AttributeSelectDocument,
    variables: { entityType: 'Job' },
    requestPolicy: 'cache-first',
  });
  const jobAttributes = jobAttributesResult.data?.attributes;

  return (
    <SideLayout>
      <SideLayout.Head
        leftSlot={<Link to='..' className='x' />}
        rightSlot={<AutomationMenu />}
      >
        {automation.name}
      </SideLayout.Head>
      <SideLayout.Body>
        <StatusBadge value={automation.status} className='mb-5' />

        <div
          className={classNames('flex flex-col', isMobile ? 'gap-3' : 'gap-5')}
        >
          <ViewCard label='Where'>
            <WhereBubble automation={automation} />
          </ViewCard>

          <ViewCard label='When'>
            <WhenBubble automation={automation} />
          </ViewCard>

          {(automation.owners?.length || automation.assignees?.length) && (
            <ViewCard label='Who'>
              <WhoBubble automation={automation} />
            </ViewCard>
          )}

          <ViewCard label='What'>
            <WhatBubble
              automation={automation}
              templateData={templateData}
              siteAttributes={siteAttributes}
              jobAttributes={jobAttributes}
            />
          </ViewCard>
        </div>
      </SideLayout.Body>
    </SideLayout>
  );
}
